import { Injectable, NgZone } from '@angular/core';
import { Subject, Observable } from 'rxjs';

export interface StorageEvent {
  type: 'set' | 'remove' | 'clear';
  key?: string;
  value?: any;
  external: boolean; // пришло из другой вкладки или локально
}

interface StorageMessage {
  type: 'set' | 'remove' | 'clear' | 'request_state' | 'state_response';
  key?: string;
  value?: string;
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  private channel = new BroadcastChannel('session-storage-sync');
  private storageEvents$ = new Subject<StorageEvent>();

  // Ключи, которые нужно синхронизировать при старте новой вкладки
  private keysToSync = ['products', 'certificates', 'productsCount', 'promocode', 'giftCards'];

  constructor(private zone: NgZone) {

    // Подписка на сообщения от других вкладок
    this.channel.onmessage = (event: MessageEvent<StorageMessage>) => {
      const msg = event.data;

      this.zone.run(() => {
        switch(msg.type) {
          case 'set':
          case 'remove':
          case 'clear':
            this.handleStandardMessage(msg, true);
            break;

          case 'request_state':
            // Отправляем текущие значения sessionStorage
            this.keysToSync.forEach(key => {
              const value = sessionStorage.getItem(key);
              if (value) {
                this.channel.postMessage({ type: 'state_response', key, value });
              }
            });
            break;

          case 'state_response':
            if (msg.key && msg.value && !sessionStorage.getItem(msg.key)) {
              sessionStorage.setItem(msg.key, msg.value);
              this.emitEvent('set', msg.key, JSON.parse(msg.value), true);
            }
            break;
        }
      });
    };

    // При старте новой вкладки сразу запрашиваем состояние у других вкладок
    this.channel.postMessage({ type: 'request_state' });
  }

  /** Подписка на события storage */
  watch(): Observable<StorageEvent> {
    return this.storageEvents$.asObservable();
  }

  /** Стандартная обработка set/remove/clear */
  private handleStandardMessage(msg: StorageMessage, external = false) {
    switch(msg.type) {
      case 'set':
        if (msg.key && msg.value) {
          sessionStorage.setItem(msg.key, msg.value);
          this.emitEvent('set', msg.key, JSON.parse(msg.value), external);
        }
        break;
      case 'remove':
        if (msg.key) {
          sessionStorage.removeItem(msg.key);
          this.emitEvent('remove', msg.key, null, external);
        }
        break;
      case 'clear':
        sessionStorage.clear();
        this.emitEvent('clear', undefined, null, external);
        break;
    }
  }

  private emitEvent(type: StorageEvent['type'], key?: string, value?: any, external = false) {
    this.storageEvents$.next({ type, key, value, external });
  }

  /** Методы для работы с sessionStorage */
  setItem(item: any, name: string) {
    const value = JSON.stringify(item);
    sessionStorage.setItem(name, value);

    this.channel.postMessage({ type: 'set', key: name, value });
    this.emitEvent('set', name, item, false);
  }

  getItemJSON(name: string): string | null {
    return sessionStorage.getItem(name);
  }

  getItem(name: string, nullableValue: string): any {
    return JSON.parse(sessionStorage.getItem(name) || nullableValue);
  }

  deleteItem(name: string) {
    sessionStorage.removeItem(name);

    this.channel.postMessage({ type: 'remove', key: name });
    this.emitEvent('remove', name, null, false);
  }

  clear() {
    sessionStorage.clear();

    this.channel.postMessage({ type: 'clear' });
    this.emitEvent('clear', undefined, null, false);
  }
}
